/* The sync (`27` section 6, `15` section 3.15a): each record on this phone
 * sent to the API while there is a network and a signed-in arborist, and
 * removed once the server has taken it. A refused record stays on the phone,
 * since this is the only copy of the work.
 *
 * Not run by the service worker: the token is in the page's sessionStorage
 * (`02` -> Offline work is kept on the phone, and the token is not), so only
 * an open, signed-in page can send.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { account } from "../auth";
import { changedHolds, postAudit } from "../api/fieldAudits";
import { refusalDetail } from "../api/refusal";
import type { AuditSynced } from "../api/types";
import { feetToMeters } from "../lib/format";
import { store, type Fix, type UnsyncedAudit, type UnsyncedStore } from "./unsyncedWork";

/* A record the server will take: it requires where the arborist stood (`20`
 * section 3.12b), and a position is never guessed. */
export type Ready = UnsyncedAudit & { stoodAt: Fix };

export function ready(record: UnsyncedAudit): record is Ready {
  return record.stoodAt !== null;
}

export const NO_FIX = "no position was saved with it, and it cannot sync without one";

const iso = (ms: number) => new Date(ms).toISOString();
const metres = (ft: number | undefined) => (ft === undefined ? null : feetToMeters(ft));
const position = (f: Fix) => ({ lat: f.lat, lon: f.lng, accuracy_m: f.accuracyM });

/* The request for a record: the audit as JSON, and each photo as a part named
 * by its id. A photo's id is drawn at each send rather than kept with it: a
 * resend is answered by the audit's id before any photo is stored, so a photo's
 * id need only be unique. A missed tree's values travel as the tree, never on
 * the audit, and its height as a reading, method `estimate`, since the form
 * records no tape (`20` section 3.12b). `measuredBy` is the arborist's name. */
export function syncForm(record: Ready, measuredBy: string, newId: () => string = () => crypto.randomUUID()): FormData {
  const { audit: a, missedTree: m } = record;
  const photos = [...record.photos, ...(m?.photos ?? [])].map((photo) => ({ id: newId(), photo }));
  const audit = {
    id: record.id,
    assignment_id: record.assignmentId,
    outcome: a.status,
    missed_tree: m && {
      id: m.id,
      location: { lat: m.lat, lon: m.lng },
      min_absolute_distance_m: feetToMeters(m.clearance),
      encroachment_height_m: feetToMeters(m.encroach),
      species_primary: m.species || null,
      placement: m.placement && {
        project_id: m.placement.projectId,
        span_id: m.placement.spanId,
        pole_id: m.placement.poleId,
      },
    },
    species: a.species ?? null,
    clearance_m: metres(a.clearance),
    encroachment_height_m: metres(a.encroach),
    reasons: a.reasons ?? null,
    notes: a.note || null,
    stood_at: position(record.stoodAt),
    corrected_location: a.repin ? { lat: a.repin.lat, lon: a.repin.lng } : null,
    recorded_at: iso(record.recordedAt),
    photos: photos.map(({ id, photo }) => ({
      id,
      taken_at: iso(photo.takenAt),
      taken_location: photo.location && position(photo.location),
    })),
    readings: m
      ? [{ method: "estimate", measured_by: measuredBy, measured_at: iso(record.recordedAt), height_m: feetToMeters(m.height) }]
      : [],
  };
  const form = new FormData();
  form.append("audit", JSON.stringify(audit));
  for (const { id, photo } of photos) form.append(id, photo.blob);
  return form;
}

/* What an answer means for the work: taken, so it leaves the phone; refused,
 * so it stays until the arborist asks again; no sign-in; or no server, so it
 * is sent again later. */
export type Sent =
  | { kind: "synced"; answer: AuditSynced }
  | { kind: "refused"; detail: string }
  | { kind: "signedOut" }
  | { kind: "unreachable"; detail: string };

export const EARLIER_SAVE = "Arborcision already holds an earlier save of this tree, and keeps it in place of this one";

/* One record sent. Only an answer naming this record's audit is taken: any
 * other success is not the API's, such as a page served in its place, and the
 * record stays. An answer naming it with another `recorded_at` is an earlier
 * save the server keeps in place of this one (`15` section 3.15a), so this
 * one is refused rather than removed as if it had synced. 429 and 5xx are the
 * server's state, not the record's, so they are sent again, and a gateway can
 * answer 5xx after the API has written; every other refusal is about the
 * record. */
export async function send(record: Ready, measuredBy: string, post: (form: FormData) => Promise<Response> = postAudit): Promise<Sent> {
  let response: Response;
  try {
    response = await post(syncForm(record, measuredBy));
  } catch (error) {
    return { kind: "unreachable", detail: error instanceof Error ? error.message : String(error) };
  }
  if (response.ok) {
    const answer = (await response.json().catch(() => null)) as AuditSynced | null;
    if (answer?.id !== record.id) return { kind: "unreachable", detail: `the answer (${response.status}) did not name this audit` };
    return Date.parse(answer.recorded_at) === record.recordedAt ? { kind: "synced", answer } : { kind: "refused", detail: EARLIER_SAVE };
  }
  if (response.status === 401) return { kind: "signedOut" };
  const detail = await refusalDetail(response, `refused (${response.status})`);
  return response.status === 429 || response.status >= 500 ? { kind: "unreachable", detail } : { kind: "refused", detail };
}

export interface Kept {
  id: string;
  treeKey: string;
  why: string;
  /* Refused by the server, rather than not ready to send. */
  refused: boolean;
}

export interface Round {
  synced: AuditSynced[];
  kept: Kept[];
  /* Why the round ended before the last record, or null where it reached it. */
  stopped: { signedOut: true } | { signedOut: false; why: string } | null;
}

/* Sends each record on `work` in turn, oldest first, and hands each one taken
 * to `taken`, which removes it. Each is claimed before it is sent, and one
 * that is `ready` is held against a save from then until an answer says the
 * server did not take it, since a send without one may have been taken, and
 * one taken stays held where `taken` fails to remove it. One another
 * round has claimed is left to it, and one in `skip` is not sent. A missing
 * sign-in or server ends the round, since every later record would meet it
 * too. */
export async function syncRound(
  work: Pick<UnsyncedStore, "all" | "claim" | "release">,
  sendOne: (record: Ready) => Promise<Sent>,
  taken: (record: Ready) => Promise<void>,
  skip: ReadonlySet<string> = new Set(),
): Promise<Round> {
  const round: Round = { synced: [], kept: [], stopped: null };
  for (const { id } of await work.all()) {
    if (skip.has(id)) continue;
    const record = await work.claim(id, ready);
    if (!record) continue;
    let answered = false;
    try {
      if (!ready(record)) {
        round.kept.push({ id, treeKey: record.treeKey, why: NO_FIX, refused: false });
        continue;
      }
      const sent = await sendOne(record);
      answered = sent.kind === "refused" || sent.kind === "signedOut";
      if (sent.kind === "synced") {
        await taken(record);
        round.synced.push(sent.answer);
      } else if (sent.kind === "refused") {
        round.kept.push({ id, treeKey: record.treeKey, why: sent.detail, refused: true });
      } else {
        round.stopped = sent.kind === "signedOut" ? { signedOut: true } : { signedOut: false, why: sent.detail };
        break;
      }
    } finally {
      await work.release(id, answered);
    }
  }
  return round;
}

const FIRST_WAIT_MS = 30_000;
const LONGEST_WAIT_MS = 10 * 60_000;

/* Runs rounds for the field app: when it opens, when the network returns,
 * after a save, from the Sync button, and again after a round no server
 * answered, each wait longer than the last. A round reads the records from
 * the phone rather than the screens, so one run straight after a save sends
 * it; one asked for while a round runs is run once that round ends, since a
 * record saved during it may not be on its list. Records refused in this page
 * are sent again only by `sync(true)`, the arborist's own ask. `remove` is
 * useUnsyncedWork's. */
export function useSync(remove: (id: string, recordedAt: number) => Promise<void>) {
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [sent, setSent] = useState(0);
  const [round, setRound] = useState<Round | Error | null>(null);
  const removing = useRef(remove);
  const running = useRef(false);
  const refused = useRef(new Set<string>());
  const wait = useRef(FIRST_WAIT_MS);
  const retry = useRef<number | undefined>(undefined);
  // False once the screens that run this have closed: a round still running
  // then sets no retry, which would go on with nothing to show it or stop it.
  const live = useRef(true);
  // A run asked for while a round runs, null where none was; true where the
  // arborist asked.
  const again = useRef<boolean | null>(null);
  useEffect(() => {
    removing.current = remove;
  });

  const sync = useCallback(
    async (asked = false) => {
      const who = account();
      if (!who || !navigator.onLine) return;
      if (running.current) {
        again.current = (again.current ?? false) || asked;
        return;
      }
      running.current = true;
      window.clearTimeout(retry.current);
      setSyncing(true);
      setSent(0);
      try {
        const measuredBy = who.name || who.username;
        const result = await syncRound(
          await store(),
          (record) => send(record, measuredBy),
          async (record) => {
            // The open list first, so a tree leaves the queue with its work
            // rather than showing as not audited until the list is read again.
            await changedHolds(queryClient);
            await removing.current(record.id, record.recordedAt);
            setSent((n) => n + 1);
          },
          asked ? new Set() : refused.current,
        );
        const now = result.kept.filter((k) => k.refused).map((k) => k.id);
        refused.current = new Set(asked ? now : [...refused.current, ...now]);
        if (result.stopped && !result.stopped.signedOut) {
          const later = wait.current;
          wait.current = Math.min(later * 2, LONGEST_WAIT_MS);
          if (live.current) retry.current = window.setTimeout(() => void sync(), later);
        } else {
          wait.current = FIRST_WAIT_MS;
        }
        setRound(result);
      } catch (error) {
        setRound(error instanceof Error ? error : new Error(String(error)));
      } finally {
        running.current = false;
        setSyncing(false);
        const next = again.current;
        again.current = null;
        if (next !== null && live.current) void sync(next);
      }
    },
    [queryClient],
  );

  // A subscription to the phone's network: its return is when held work can
  // go, and the wait before a retry starts again from the first.
  useEffect(() => {
    const online = () => {
      wait.current = FIRST_WAIT_MS;
      void sync();
    };
    live.current = true;
    window.addEventListener("online", online);
    void sync();
    return () => {
      live.current = false;
      window.removeEventListener("online", online);
      window.clearTimeout(retry.current);
    };
  }, [sync]);

  return { sync, syncing, sent, round };
}
